/** Hand routes to another operator for a run of dates; those days go on that operator's board. */
import React, { useMemo, useState } from "react"

import { format, isBefore, startOfDay } from "date-fns"
import { CalendarIcon, Handshake, Loader2, Search, Trash2, X } from "lucide-react"
import { DateRange } from "react-day-picker"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  type OperatorAllocationRow,
  useAllocateOperator,
  useOperatorAllocations,
  useOperators,
  useRemoveOperatorAllocation,
  useRoutesPlanned,
} from "@/hooks/useCollections"
import { formatAllocationDates } from "@/lib/operatorAllocations"
import { isRetiredPlannedRoute, todayDateOnly } from "@/lib/plannedRouteLifecycle"
import { cn } from "@/lib/utils"
import type { RoutePlanned } from "@/types/collections"

interface SubcontractorsButtonProps {
  /** Opens with this route picked, listing only its allocations. */
  routeId?: string
  className?: string
}

const formatRange = (from_date: string, to_date: string) =>
  formatAllocationDates({ from_date, to_date })

const SubcontractorsButton: React.FC<SubcontractorsButtonProps> = ({ routeId, className }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [operator, setOperator] = useState("")
  const [dates, setDates] = useState<DateRange | undefined>()
  const [note, setNote] = useState("")
  const [removingId, setRemovingId] = useState<string | null>(null)

  const { data: routesPlannedData, isLoading: routesLoading } = useRoutesPlanned(undefined, open)
  const { data: operatorsData } = useOperators(open)
  const { data: allocations = [] } = useOperatorAllocations(routeId)
  const allocate = useAllocateOperator()
  const remove = useRemoveOperatorAllocation()

  const today = todayDateOnly()
  const routes = useMemo(
    () =>
      ((routesPlannedData?.data as RoutePlanned[]) ?? []).filter(
        (route) => route.status === "live" && !isRetiredPlannedRoute(route, today),
      ),
    [routesPlannedData, today],
  )

  const visibleRoutes = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return routes
    return routes.filter(
      (route) =>
        route.code.toLowerCase().includes(term) ||
        (route.depot ?? "").toLowerCase().includes(term) ||
        (route.operator ?? "").toLowerCase().includes(term),
    )
  }, [routes, search])

  const operators = operatorsData?.data?.map((o) => o.name) ?? []

  const openPanel = () => {
    setSelected(new Set(routeId ? [routeId] : []))
    setOpen(true)
  }

  const toggleRoute = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // A route the chosen operator already runs stays ticked but is not sent; the server refuses it.
  const chosen = routes
    .filter((route) => selected.has(route.id) && route.operator !== operator)
    .map((route) => route.id)

  const from = dates?.from
  const to = dates?.to ?? dates?.from
  const canAllocate = chosen.length > 0 && !!operator && !!from && !allocate.isPending

  const handleAllocate = async () => {
    if (!canAllocate || !from || !to) return
    try {
      const rows = await allocate.mutateAsync({
        routeIds: chosen,
        operator,
        from: format(from, "yyyy-MM-dd"),
        to: format(to, "yyyy-MM-dd"),
        note: note.trim() || null,
      })
      toast.success(
        `${rows.length} route${rows.length === 1 ? "" : "s"} handed to ${operator}`,
        { description: `${formatRange(rows[0].from_date, rows[0].to_date)}.` },
      )
      setSelected(new Set(routeId ? [routeId] : []))
      setDates(undefined)
      setNote("")
    } catch (err) {
      toast.error("Could not hand over the routes", {
        description: err instanceof Error ? err.message : "Nothing was changed. Please try again.",
      })
    }
  }

  const handleRemove = async (allocation: OperatorAllocationRow) => {
    setRemovingId(allocation.id)
    try {
      await remove.mutateAsync(allocation.id)
      toast.success(`${allocation.route_code ?? "Route"} is back with its usual operator`, {
        description: `For ${formatRange(allocation.from_date, allocation.to_date)}. Days already run are unchanged.`,
      })
    } catch (err) {
      toast.error("Could not remove the allocation", {
        description: err instanceof Error ? err.message : "Nothing was changed. Please try again.",
      })
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        aria-pressed={open}
        onClick={openPanel}
        className={cn(
          "h-8 border-palette-jungleGreen200 bg-white text-palette-jungleGreen700",
          open && "border-amber-200 bg-amber-50",
          className,
        )}
      >
        <Handshake size={14} className="mr-2" />
        {routeId ? "Hand over dates" : "Subcontractors"}
        {allocations.length > 0 && ` (${allocations.length})`}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" size="wide" className="flex flex-col overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Subcontracted routes</SheetTitle>
            <SheetDescription>
              Hand routes to another operator for a run of dates. Those days appear on their board
              for them to put drivers on, and the routes go back to their usual operator
              afterwards.
            </SheetDescription>
          </SheetHeader>

          <section className="flex-shrink-0 space-y-3 rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label className="text-xs text-palette-jungleGreen600">Operator *</Label>
                <Select value={operator} onValueChange={setOperator}>
                  <SelectTrigger className="h-9 border-palette-jungleGreen200 bg-white text-sm">
                    <SelectValue placeholder="Choose operator" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {operators.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-palette-jungleGreen600">Dates *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 w-full justify-start border-palette-jungleGreen200 bg-white text-sm"
                    >
                      <CalendarIcon size={14} className="mr-2 text-palette-jungleGreen500" />
                      {from && to
                        ? formatRange(format(from, "yyyy-MM-dd"), format(to, "yyyy-MM-dd"))
                        : "Select dates"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent variant="plain" align="end">
                    <Calendar
                      mode="range"
                      selected={dates}
                      onSelect={setDates}
                      numberOfMonths={2}
                      disabled={(date) => isBefore(date, startOfDay(new Date()))}
                      className="bg-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs text-palette-jungleGreen600">
                Routes * {chosen.length > 0 && `(${chosen.length} chosen)`}
              </Label>
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-palette-jungleGreen400"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by code, depot or operator"
                  aria-label="Search routes"
                  className="h-8 border-palette-jungleGreen200 bg-white pl-8 pr-8 text-xs"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 hover:text-palette-jungleGreen700"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              <div className="max-h-56 divide-y divide-palette-jungleGreen100 overflow-y-auto rounded-md border border-palette-jungleGreen200 bg-white">
                {visibleRoutes.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-palette-jungleGreen500">
                    {routesLoading
                      ? "Loading routes…"
                      : routes.length === 0
                        ? "No live routes."
                        : `No live route matches “${search}”.`}
                  </p>
                ) : (
                  visibleRoutes.map((route) => {
                    const alreadyTheirs = !!operator && route.operator === operator
                    return (
                      <label
                        key={route.id}
                        className={cn(
                          "flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-palette-jungleGreen000",
                          alreadyTheirs && "cursor-not-allowed opacity-50",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(route.id) && !alreadyTheirs}
                          disabled={alreadyTheirs}
                          onChange={() => toggleRoute(route.id)}
                          className="h-3.5 w-3.5 rounded border-palette-jungleGreen300 accent-amber-500"
                        />
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-palette-jungleGreen900">
                          {route.code}
                        </span>
                        <span className="shrink-0 text-[11px] text-palette-jungleGreen500">
                          {route.operator || "HIVED"}
                          {route.depot?.trim() && ` · ${route.depot}`}
                        </span>
                      </label>
                    )
                  })
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs text-palette-jungleGreen600">Note</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional, e.g. why or who agreed it"
                className="h-8 border-palette-jungleGreen200 bg-white text-xs"
              />
            </div>

            <Button
              variant="confirm"
              className="w-full"
              disabled={!canAllocate}
              onClick={handleAllocate}
            >
              {allocate.isPending ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Handing over…
                </>
              ) : (
                <>
                  <Handshake size={14} className="mr-1.5" />
                  {chosen.length > 0 && operator
                    ? `Hand ${chosen.length} route${chosen.length === 1 ? "" : "s"} to ${operator}`
                    : "Hand over routes"}
                </>
              )}
            </Button>
          </section>

          <section className="flex-shrink-0">
            <h4 className="mb-2 text-xs font-medium text-palette-jungleGreen600">
              Current and upcoming
            </h4>
            {allocations.length === 0 ? (
              <p className="py-4 text-center text-xs text-palette-jungleGreen500">
                {routeId ? "This route is not handed over on any date." : "No routes handed over."}
              </p>
            ) : (
              <div className="divide-y divide-palette-jungleGreen200 rounded-md border border-palette-jungleGreen200 bg-white">
                {allocations.map((allocation) => (
                  <div key={allocation.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-palette-jungleGreen900">
                        {allocation.route_code} → {allocation.operator}
                      </p>
                      <p className="truncate text-xs text-palette-jungleGreen500">
                        {formatRange(allocation.from_date, allocation.to_date)}
                        {allocation.note && ` · ${allocation.note}`}
                        {allocation.created_by && ` · ${allocation.created_by}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 gap-1.5 text-xs"
                      disabled={removingId === allocation.id}
                      onClick={() => handleRemove(allocation)}
                    >
                      {removingId === allocation.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Trash2 size={13} />
                      )}
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default SubcontractorsButton
